// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// styles
import './styles/graphic.scss';

/* develblock:start */
import * as d3 from 'd3';
import { format } from 'd3-format';
import meta from './data/meta.json';
import { stylePremium } from './utils/helpers';

import './styles/graphic.scss';

// formatD3Time();
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const fmtComma = format(',');
const fmtSI = format('.1s');
const fmtThou = format('~s');
const getHeadline = (cur, prev) => {
  if (cur.amt === 0) return null;
  const rateDir = cur.rate - prev.rate;
  return [
    'US economy',
    cur.amt > 0 ? 'added' : 'lost',
    fmtComma(cur.amt),
    `job${cur.amt !== 1 ? 's' : ''}`,
    `in ${cur.month},`,
    'unemployment rate',
    `${(rateDir < 0) ? 'dropped to' : (rateDir > 0) ? 'rose to' : 'remains at'}`,
    `${cur.rate}%`,
  ].join(' ');
};


const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}


let chartData;
let minValue;
let maxValue;
let minDomain;
let maxDomain;


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};


const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

// How to draw the cells
const drawCell = (xScale, isMobile) => (d) => {
  // drawing the values based
  if (d.column === 'amt') {
    let l = xScale(0);
    const dir = d.value < 0 ? -1 : 1;
    const dirClass = (dir < 0) ? 'negative' : 'positive';
    let w;

    let dims; let
      right;
    if (d.value < 0) {
      right = xScale(0);
      w = xScale(0) - xScale(d.value);
      dims = [
        `left: ${xScale(d.value)}%`,
        `width:${w}%`,
      ];
    } else {
      right = xScale(d.value) - xScale(0);
      w = right;
      l = xScale(d.value);
      dims = [
        `left: ${xScale(0)}%`,
        `width: ${w}%`,
      ];
    }
    const pos = dims.join(';');
    const offset = (isMobile) ? 4 : 4;

    const fmt = (isMobile) ? fmtThou : fmtThou;
    return `<div class="graphic-bar-row">
      <span class="graphic-bar ${dirClass}" style="${pos}" data-w="${w}"></span>
      <span class="graphic-bar-num ${dirClass}" style="left:${l}%;top:${offset}px">${fmt(d.value).toUpperCase()}
    </div>`;
  }
  // drawing the text based
  return `${d.value}`;
};

let data;
let publishMonth;
let industrySummary;
let lastUpdatedStr;
let currentMonth;

d3.json('https://ix.cnn.io/data/jobs-next/employment_situation_latest.json').then((jsondata) => {
  // Create our chart data from the raw json data
  const curYear = '';
  data = jsondata.industries_data.map(
    (d, i) => {
      const dt = d3.timeParse('%Y-%m-%d')(d.date);
      return {
        ...d,
        amt: d.change,
        dt,
        s: dt.getTime(),
      };
    },
  )
    .sort((a, b) => a.dt.getTime() - b.dt.getTime());

  const maxDate = d3.max(data, d => d.s);

  data = data.filter(
    d => d.s === maxDate,
  )
    .sort((a, b) => b.amt - a.amt);
  minValue = d3.min(data, d => d.amt);
  maxValue = d3.max(data, d => d.amt);

  publishMonth = d3.timeFormat('%b %-d, %Y.')(d3.timeParse('%Y-%m-%d')(jsondata.publish));
  industrySummary = jsondata.industry_summary;
  lastUpdatedStr = jsondata.last_updated_str;
  currentMonth = jsondata.month;

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const initialHeight = 400;
  const isMobile = initialWidth <= 500;

  const margins = {
    top: 30,
    right: 20,
    bottom: 25,
    left: (isMobile) ? 50 : 75,
  };

  const width = initialWidth - margins.right - margins.left;

  const height = 400;
  const padding = 0.15;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const table = d3.select('#graphic').append('table').attr('class', 'graphic-table');
  const thead = table.append('thead');
  const	tbody = table.append('tbody');

  const columns = ['industry_name', 'amt'];

  // Check and set the Dek
  if (industrySummary && industrySummary !== '') {
    d3.select('h2.dek').html(industrySummary);
  }

  const timeStampFootnoote = d3.select('p#timestamp')
    .html(`Notes: Employment numbers reflect monthly change in non-farm jobs and is seasonally adjusted. Figures from two most recent months are preliminary. Data current through ${currentMonth} as of ${lastUpdatedStr}.`);

  const xScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([0, 100]);

  window.x = xScale;

  // append the header row
  thead.append('tr')
	  .selectAll('th')
	  .data(columns).enter()
	  .append('th')
    .attr('class', column => column.split(' ').join(''))
	    .text(column => column);

  // create a row for each object in the data
  const rows = tbody.selectAll('tr')
	  .data(data)
	  .enter()
	  .append('tr');

  // create a cell in each row for each column
  const cells = rows.selectAll('td')
	  .data(row => columns.map(column => ({ column, value: row[column] })))
	  .enter();

  cells
    .append('td')
    .attr('valign', 'center')
    .html(drawCell(xScale, isMobile));


  window.pymChild.sendHeight();

  d3.select('#root')
    .attr('class', 'ready');
}
